// Talks to willow-saas at /account/*. In production, Vercel rewrites
// /account/(.*) to https://api.willow.tech/account/$1 so the browser
// treats this as same-origin and the session cookie set by api.willow.tech
// flows on every subsequent fetch. In dev, vite.config.ts proxies
// /account to http://127.0.0.1:3050 for the same effect.

import { capitalize } from './errors';

export type SubgroveType = 'indexing' | 'data_storage' | 'file_storage' | 'unknown';

export interface SaasPlan {
  id: string;
  display_name: string;
  req_per_day: number;
  rate_limit_rps: number;
  monthly_cents: number;
  stripe_price_id: string | null;
  is_public: boolean;
  data_storage_included: number;
  data_storage_extra_cents: number | null;
  indexing_included: number;
  indexing_extra_cents: number | null;
  file_storage_included: number;
  file_storage_extra_cents: number | null;
  file_storage_gb_included: number;
  file_storage_per_gb_cents: number | null;
  write_request_weight: number;
}

export interface SaasSubgrove {
  id: string;
  subgrove_id: string;
  subgrove_type: SubgroveType;
  monthly_cents: number;
  is_included: boolean;
  registered_at: string;
  cancelled_at: string | null;
}

export interface SaasSubgrovesResponse {
  plan: SaasPlan;
  subgroves: SaasSubgrove[];
  total_monthly_cents: number;
}

export interface SaasUser {
  id: string;
  /** Null for accounts opened by an agent's wallet (x402). */
  email: string | null;
  /** The old storage-product plan; the RPC plan is in `/account/rpc`. */
  plan_id: string;
  email_verified: boolean;
  created_at: string;
  wallet_address?: string | null;
  has_password?: boolean;
  github_linked?: boolean;
  google_linked?: boolean;
}

export interface SaasApiKey {
  id: string;
  key_prefix: string;
  name: string;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface SaasApiKeyCreated extends SaasApiKey {
  key: string;
}

export interface SaasUsageDay {
  day: string;
  request_count: number;
}

export interface SaasUsage {
  plan: SaasPlan;
  today: number;
  history: SaasUsageDay[];
}

/** An error from willow-saas. `message` is shown as is, so it is capitalized
 *  here once for every caller. */
class SaasError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(capitalize(message));
    this.status = status;
    this.code = code;
  }
}

/** `fetch` against willow-saas under `/account`, with its JSON error shape
 *  (`{ error, code? }`) turned into a `SaasError`. */
export async function saasCall<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`/account${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  return readSaas<T>(res);
}

/** A willow-saas response's JSON body, or its error as a `SaasError`. */
export async function readSaas<T>(res: Response): Promise<T> {
  const text = await res.text();
  let body = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    // A proxy or gateway error page, not saas JSON.
    if (res.ok) throw new SaasError('unexpected response from the server', res.status);
  }
  if (!res.ok) {
    const msg = (body && (body.error || body.message)) || `HTTP ${res.status}`;
    const code = body && typeof body.code === 'string' ? body.code : undefined;
    throw new SaasError(msg, res.status, code);
  }
  return body as T;
}

export function saasSignup(
  email: string,
  password: string,
  newsletterOptIn: boolean = false,
  turnstileToken?: string,
  plan?: string,
): Promise<SaasUser> {
  return saasCall<SaasUser>('/signup', {
    method: 'POST',
    body: JSON.stringify({
      email,
      password,
      newsletter_opt_in: newsletterOptIn,
      turnstile_token: turnstileToken,
      plan,
    }),
  });
}

export function saasLogin(
  email: string,
  password: string,
  turnstileToken?: string,
): Promise<SaasUser> {
  return saasCall<SaasUser>('/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, turnstile_token: turnstileToken }),
  });
}

export async function saasLogout(): Promise<void> {
  await fetch('/account/logout', { method: 'POST', credentials: 'include' });
}

export function saasMe(): Promise<SaasUser> {
  return saasCall<SaasUser>('/me');
}

export function saasListKeys(): Promise<SaasApiKey[]> {
  return saasCall<SaasApiKey[]>('/keys');
}

export function saasCreateKey(name?: string): Promise<SaasApiKeyCreated> {
  return saasCall<SaasApiKeyCreated>('/keys', {
    method: 'POST',
    body: JSON.stringify({ name: name ?? 'default' }),
  });
}

export async function saasRevokeKey(id: string): Promise<void> {
  const res = await fetch(`/account/keys/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 404) {
    throw new SaasError(`HTTP ${res.status}`, res.status);
  }
}

export function saasUsage(): Promise<SaasUsage> {
  return saasCall<SaasUsage>('/usage');
}

export function saasListPlans(): Promise<SaasPlan[]> {
  return saasCall<SaasPlan[]>('/plans');
}

export function saasListSubgroves(): Promise<SaasSubgrovesResponse> {
  return saasCall<SaasSubgrovesResponse>('/subgroves');
}

export async function saasCancelSubgrove(id: string): Promise<void> {
  const res = await fetch(`/account/subgroves/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 404) {
    throw new SaasError(`HTTP ${res.status}`, res.status);
  }
}

export async function saasPasswordResetRequest(
  email: string,
  turnstileToken?: string,
): Promise<void> {
  await saasCall<unknown>('/password-reset/request', {
    method: 'POST',
    body: JSON.stringify({ email, turnstile_token: turnstileToken }),
  });
}

export interface SaasUpgradeResult {
  status: string;
  plan_id?: string;
  url?: string;
  billing?: string;
  ends_at?: number;
}

export function saasUpgrade(planId: string): Promise<SaasUpgradeResult> {
  return saasCall<SaasUpgradeResult>('/upgrade', {
    method: 'POST',
    body: JSON.stringify({ plan_id: planId }),
  });
}

export async function saasVerifyEmail(token: string): Promise<void> {
  await saasCall<unknown>('/verify-email', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function saasResendVerification(
  email: string,
  turnstileToken?: string,
): Promise<void> {
  await saasCall<unknown>('/verify-email/resend', {
    method: 'POST',
    body: JSON.stringify({ email, turnstile_token: turnstileToken }),
  });
}

export async function saasPasswordResetConfirm(
  token: string,
  newPassword: string,
): Promise<void> {
  await saasCall<unknown>('/password-reset/confirm', {
    method: 'POST',
    body: JSON.stringify({ token, new_password: newPassword }),
  });
}

/** Set the password on an account Willow set up, from the emailed invite
 *  link. saas verifies the email and signs in: the session cookie comes back. */
export function saasAcceptInvite(token: string, password: string): Promise<SaasUser> {
  return saasCall<SaasUser>('/invite/accept', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

export { SaasError };
